import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  BellRing, CheckCheck, RefreshCw, Sparkles, Inbox as InboxIcon,
  Wrench, ClipboardList, LifeBuoy, FileText, Receipt, ScanText,
  AlertCircle, Camera, MessageSquare, Image as ImageIcon, Settings,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import { enableWebPush, pushSupported } from "../lib/webpush";

/*
 * AlertsPage — directed unread notifications feed (Wave 19 #83).
 *
 * Mirrors the WhatsApp "messages for me" experience: every notification
 * delivered to the current user lands here, the priority banner pulses
 * rose-red while count > 0, and the user can mark items read or open
 * the source entity in one click.
 */

const KIND_META = {
  // Job card events
  jobcard_assigned:        { Icon: Wrench,         color: "#F59E0B", label: "Assigned" },
  jobcard_on_site_complete:{ Icon: CheckCheck,     color: "#34D399", label: "On-site complete" },
  jobcard_photo:           { Icon: ImageIcon,      color: "#1FB6C1", label: "Photo" },
  jobcard_comment:         { Icon: MessageSquare,  color: "#A78BFA", label: "Comment" },
  jobcard_phase:           { Icon: Sparkles,       color: "#F59E0B", label: "Status change" },
  jobcard_updated:         { Icon: ClipboardList,  color: "#60A5FA", label: "Updated" },
  // Tickets
  ticket_assigned:         { Icon: LifeBuoy,       color: "#F43F5E", label: "Assigned" },
  ticket_from_tech:        { Icon: AlertCircle,    color: "#F43F5E", label: "Tech raised" },
  ticket_comment:          { Icon: MessageSquare,  color: "#A78BFA", label: "Comment" },
  ticket_attachment:       { Icon: ImageIcon,      color: "#1FB6C1", label: "Attachment" },
  ticket_comment_staff:    { Icon: MessageSquare,  color: "#A78BFA", label: "Reply" },
  // Tasks
  task_assigned:           { Icon: ClipboardList,  color: "#34D399", label: "Task" },
  // Sales docs
  sales_doc_quote:         { Icon: FileText,       color: "#1FB6C1", label: "Quote" },
  sales_doc_sales_order:   { Icon: FileText,       color: "#1FB6C1", label: "Order" },
  sales_doc_invoice:       { Icon: Receipt,        color: "#A78BFA", label: "Invoice" },
  sales_doc_statement:     { Icon: Receipt,        color: "#A78BFA", label: "Statement" },
  sales_doc_credit_note:   { Icon: InboxIcon,      color: "#34D399", label: "Credit" },
  // Portal events
  portal_order_received:   { Icon: InboxIcon,      color: "#F43F5E", label: "Portal order" },
  order_received:          { Icon: InboxIcon,      color: "#F43F5E", label: "Order" },
  portal_ticket_created:   { Icon: LifeBuoy,       color: "#F43F5E", label: "Portal ticket" },
  portal_ticket_comment:   { Icon: MessageSquare,  color: "#F43F5E", label: "Client reply" },
  portal_ticket_attachment:{ Icon: ImageIcon,      color: "#F43F5E", label: "Client upload" },
  portal_gallery_upload:   { Icon: Camera,         color: "#F43F5E", label: "Photo from client" },
  portal_doc_added:        { Icon: FileText,       color: "#1FB6C1", label: "Doc added" },
  portal_equipment_added:  { Icon: Wrench,         color: "#F59E0B", label: "Equipment" },
  portal_jobcard_created:  { Icon: Wrench,         color: "#F59E0B", label: "Job card" },
  portal_ticket_opened:    { Icon: LifeBuoy,       color: "#A78BFA", label: "Ticket opened" },
  // OCR
  ocr_ready:               { Icon: ScanText,       color: "#34D399", label: "OCR ready" },
  ocr_failed:              { Icon: AlertCircle,    color: "#F43F5E", label: "OCR failed" },
  // Briefs
  daily_brief:             { Icon: Sparkles,       color: "#1FB6C1", label: "Brief" },
  // Lead
  lead_followup:           { Icon: ClipboardList,  color: "#F59E0B", label: "Follow-up" },
};

function fmtRelative(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AlertsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("unread"); // unread | all
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [pushReady, setPushReady] = useState(false);

  const load = useCallback(async (opts) => {
    // Iter 173 — skip the loading skeleton on background polls so the
    // user's scroll position is preserved while they read notifications.
    const silent = !!opts?.silent;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=200");
      // 2026-02-16 (Dirk bug): the AlertsPage and NotificationsBell
      // both poll /api/notifications, but this page used to strip
      // daily_brief items "because they live in Inbox" — except there's
      // no Inbox page in the live shell. So the bell showed briefs +
      // a red count, /alerts showed nothing → "bell and alerts don't
      // sync". Fix: render everything the bell shows. If we ever
      // introduce a real Inbox view, briefs can live in BOTH places
      // (they're notifications, not files).
      setItems(data?.items || []);
    } catch (_e) {
      /* noop */
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Aggressive 15s poll while page is foreground (matches the priority
  // banner's heartbeat for the WhatsApp-feel). Iter 173: pass silent
  // so the page doesn't blank while the user is reading.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load({ silent: true });
    }, 15_000);
    return () => clearInterval(id);
  }, [load]);
  // Web Push subscription state.
  useEffect(() => {
    (async () => {
      try {
        if (!pushSupported()) return;
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          setPushReady(!!sub && Notification.permission === "granted");
        }
      } catch { /* noop */ }
    })();
  }, []);

  const filtered = filter === "unread" ? items.filter((i) => !i.is_read) : items;

  const markRead = async (n) => {
    setBusy(n.id);
    try {
      await api.post(`/notifications/${n.id}/read`);
      setItems((rows) => rows.map((r) => (r.id === n.id ? { ...r, is_read: true } : r)));
      // 2026-02-20 (iter 172) — broadcast so the global NotificationsBell
      // refreshes immediately instead of waiting for its 45s poll.
      try { window.dispatchEvent(new CustomEvent("hv:notifications-mutated")); } catch { /* old */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark as read.");
    } finally {
      setBusy("");
    }
  };

  const markAllRead = async () => {
    setBusy("__all__");
    try {
      await api.post("/notifications/read-all");
      setItems((rows) => rows.map((r) => ({ ...r, is_read: true })));
      try { window.dispatchEvent(new CustomEvent("hv:notifications-mutated")); } catch { /* old */ }
      toast.success("All caught up.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark all read.");
    } finally {
      setBusy("");
    }
  };

  const open = async (n) => {
    if (!n.is_read) await markRead(n);
    if (n.link) navigate(n.link);
  };

  const enablePush = async () => {
    try {
      await enableWebPush();
      setPushReady(true);
      toast.success("Push notifications enabled.");
    } catch (e) {
      toast.error(e?.message || "Could not enable push notifications.");
    }
  };

  const testPush = async () => {
    try {
      await api.post("/push/test");
      toast.success("Test push sent — check your device.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test push.");
    }
  };

  const unreadCount = items.filter((i) => !i.is_read).length;

  return (
    <div className="space-y-4" data-testid="alerts-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-2">
            <BellRing className="w-7 h-7 text-rose-300" />
            Alerts
          </h1>
          <p className="text-xs text-[#a0aabf] mt-1">
            Real-time messages directed at you — job updates, ticket replies, portal activity. Mirrors the priority banner.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded border border-white/10 overflow-hidden text-xs" data-testid="alerts-filter">
            <button
              onClick={() => setFilter("unread")}
              className={`px-3 py-1.5 ${filter === "unread" ? "bg-rose-500/20 text-rose-200" : "text-[#9aa3b5] hover:bg-white/5"}`}
              data-testid="alerts-filter-unread"
            >
              Unread {unreadCount > 0 && `· ${unreadCount}`}
            </button>
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1.5 border-l border-white/10 ${filter === "all" ? "bg-white/10 text-white" : "text-[#9aa3b5] hover:bg-white/5"}`}
              data-testid="alerts-filter-all"
            >
              All
            </button>
          </div>
          <button onClick={load} className="hv-btn hv-btn-ghost text-xs" data-testid="alerts-refresh" disabled={loading}>
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={markAllRead}
            disabled={unreadCount === 0 || busy === "__all__"}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-40"
            data-testid="alerts-markall"
          >
            <CheckCheck className="w-3 h-3" /> Mark all read
          </button>
        </div>
      </div>

      {/* Push subscription strip */}
      {pushSupported() && !pushReady && (
        <div
          className="hv-card border-amber-400/40 bg-amber-500/[0.06] p-3 flex items-center justify-between flex-wrap gap-2"
          data-testid="alerts-push-prompt"
        >
          <div className="text-xs text-amber-100 flex items-center gap-2">
            <BellRing className="w-3.5 h-3.5" />
            Enable push notifications to get instant alerts even when the app is closed.
          </div>
          <button onClick={enablePush} className="hv-btn hv-btn-primary text-xs" data-testid="alerts-push-enable">
            Enable on this device
          </button>
        </div>
      )}
      {pushReady && (
        <div
          className="hv-card border-emerald-400/40 bg-emerald-500/[0.06] p-3 flex items-center justify-between flex-wrap gap-2"
          data-testid="alerts-push-ready"
        >
          <div className="text-xs text-emerald-200 flex items-center gap-2">
            <CheckCheck className="w-3.5 h-3.5" />
            Push notifications enabled on this device.
          </div>
          <button onClick={testPush} className="hv-btn hv-btn-ghost text-xs" data-testid="alerts-push-test">
            <Settings className="w-3 h-3" /> Send test
          </button>
        </div>
      )}

      {loading ? (
        <div className="hv-card p-6 text-sm text-[#a0aabf]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hv-card p-10 text-center" data-testid="alerts-empty">
          <CheckCheck className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
          <h3 className="font-display text-xl font-bold mb-1">All caught up</h3>
          <p className="text-xs text-[#a0aabf]">
            {filter === "unread" ? "No unread alerts." : "No alerts yet."}
          </p>
        </div>
      ) : (
        <div className="hv-card p-0 overflow-hidden divide-y divide-white/5">
          {filtered.map((n) => {
            const meta = KIND_META[n.kind] || { Icon: BellRing, color: "#9aa3b5", label: n.kind || "alert" };
            const Icon = meta.Icon;
            return (
              <div
                key={n.id}
                className={`flex items-start gap-3 p-3 hover:bg-white/[0.03] transition cursor-pointer ${
                  n.is_read ? "opacity-70" : "border-l-2"
                }`}
                style={!n.is_read ? { borderLeftColor: meta.color } : undefined}
                onClick={() => open(n)}
                data-testid={`alert-row-${n.id}`}
              >
                <div
                  className="w-8 h-8 rounded-md border flex items-center justify-center shrink-0"
                  style={{ borderColor: `${meta.color}55`, background: `${meta.color}1a` }}
                >
                  <Icon className="w-4 h-4" style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded"
                      style={{ color: meta.color, background: `${meta.color}15`, border: `1px solid ${meta.color}33` }}
                    >
                      {meta.label}
                    </span>
                    <span className="text-sm font-medium text-white truncate">{n.title}</span>
                    {!n.is_read && (
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-400 inline-block" data-testid={`alert-unread-dot-${n.id}`} />
                    )}
                  </div>
                  {n.body && (
                    <div className="text-xs text-[#a0aabf] mt-0.5 line-clamp-2">{n.body}</div>
                  )}
                  <div className="text-[10px] text-[#647087] mt-1">
                    {n.by_user_name ? <span>{n.by_user_name} · </span> : null}
                    {fmtRelative(n.created_at)}
                  </div>
                </div>
                {!n.is_read && (
                  <button
                    onClick={(e) => { e.stopPropagation(); markRead(n); }}
                    disabled={busy === n.id}
                    className="hv-btn hv-btn-ghost text-[10px] px-2 py-1 shrink-0 disabled:opacity-40"
                    data-testid={`alert-markread-${n.id}`}
                    title="Mark as read"
                  >
                    <CheckCheck className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
